import { getConsent, hasConsent } from './storage'
import { analyticsSafeUrl, safeAnalyticsParameters } from '../analyticsPrivacy'
import { isWebMeasurementAllowed } from '../measurementPolicy'

type ConsentValue = 'granted' | 'denied'

type GoogleConsentState = {
  analytics: boolean
  marketing: boolean
}

type Gtag = (...args: unknown[]) => void

declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: Gtag
  }
}

const GOOGLE_TAG_SCRIPT_ID = 'optioneer-google-tag'

let defaultConsentSet = false
let googleTagConfigured = false
let googleTagScriptRequested = false
const sentTrialConversionIds = new Set<string>()
const TRIAL_CONVERSIONS_KEY = 'optioneer_sent_trial_conversions_v1'

function getGtag(): Gtag | null {
  if (typeof window === 'undefined') return null

  window.dataLayer = window.dataLayer || []
  if (!window.gtag) {
    window.gtag = function gtag() {
      // Google tag consumes the function's array-like arguments object.
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer?.push(arguments)
    }
  }

  return window.gtag
}

function consentValue(granted: boolean): ConsentValue {
  return granted ? 'granted' : 'denied'
}

/**
 * Establish Consent Mode v2 defaults before any Google destination is configured.
 * This creates only the local queue; it does not load Google's script.
 */
export function initializeGoogleConsentMode(): void {
  if (defaultConsentSet) return

  const gtag = getGtag()
  if (!gtag) return

  gtag('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  })
  defaultConsentSet = true
}

/** Map Optioneer's existing analytics/marketing categories to Consent Mode v2. */
export function updateGoogleConsent(state: GoogleConsentState): void {
  initializeGoogleConsentMode()
  const gtag = getGtag()
  if (!gtag) return

  const advertisingConsent = consentValue(state.marketing)
  gtag('consent', 'update', {
    analytics_storage: consentValue(state.analytics),
    ad_storage: advertisingConsent,
    ad_user_data: advertisingConsent,
    ad_personalization: advertisingConsent,
  })
}

function currentConsentState(): GoogleConsentState {
  const consent = getConsent()
  return {
    analytics: consent?.categories.analytics === true,
    marketing: consent?.categories.marketing === true,
  }
}

function requestGoogleTagScript(primaryId: string): void {
  if (googleTagScriptRequested || typeof document === 'undefined') return

  const existingScript = document.querySelector<HTMLScriptElement>(
    'script[src^="https://www.googletagmanager.com/gtag/js"]'
  )
  if (!existingScript) {
    const script = document.createElement('script')
    script.id = GOOGLE_TAG_SCRIPT_ID
    script.async = true
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(primaryId)}`
    document.head.appendChild(script)
  }

  googleTagScriptRequested = true
}

/**
 * Load one gtag.js script and configure every available Google destination.
 * Consent must be synchronized before this is called.
 */
export function initializeGoogleTag(): boolean {
  if (
    !isWebMeasurementAllowed() ||
    typeof document === 'undefined' ||
    (!hasConsent('analytics') && !hasConsent('marketing'))
  ) {
    return false
  }

  const measurementId = import.meta.env.VITE_GA_MEASUREMENT_ID || ''
  const googleAdsId = import.meta.env.VITE_GOOGLE_ADS_ID || ''
  const primaryId = measurementId || googleAdsId
  if (!primaryId) return false

  initializeGoogleConsentMode()
  updateGoogleConsent(currentConsentState())

  const gtag = getGtag()
  if (!gtag) return false

  requestGoogleTagScript(primaryId)
  if (!googleTagConfigured) {
    gtag('js', new Date())
    if (measurementId) {
      gtag('config', measurementId, {
        page_location: analyticsSafeUrl(window.location.href),
        page_referrer: analyticsSafeUrl(document.referrer || '/'),
        send_page_view: false,
        anonymize_ip: true,
        cookie_flags: 'SameSite=None;Secure',
      })
    }
    if (googleAdsId) {
      gtag('config', googleAdsId, {
        page_location: analyticsSafeUrl(window.location.href),
        page_referrer: analyticsSafeUrl(document.referrer || '/'),
      })
    }
    googleTagConfigured = true
  }

  return true
}

export function isGoogleAnalyticsConfigured(): boolean {
  return Boolean(import.meta.env.VITE_GA_MEASUREMENT_ID) && initializeGoogleTag()
}

export function sendGoogleAnalyticsPageView(path: string, title: string): void {
  if (!hasConsent('analytics') || !isGoogleAnalyticsConfigured()) return

  window.gtag?.('event', 'page_view', {
    page_path: analyticsSafeUrl(path),
    page_location: analyticsSafeUrl(`${window.location.origin}${path}`),
    page_referrer: analyticsSafeUrl(document.referrer || '/'),
    page_title: title,
    send_to: import.meta.env.VITE_GA_MEASUREMENT_ID,
  })
}

export function sendGoogleAnalyticsEvent(
  action: string,
  parameters: Record<string, string | number | undefined>
): boolean {
  if (!hasConsent('analytics') || !isGoogleAnalyticsConfigured()) return false

  window.gtag?.('event', action, {
    ...safeAnalyticsParameters(parameters),
    page_location: analyticsSafeUrl(window.location.href),
    page_referrer: analyticsSafeUrl(document.referrer || '/'),
    send_to: import.meta.env.VITE_GA_MEASUREMENT_ID,
  })
  return true
}

/**
 * Send a server-verified Stripe trial directly to Google Ads.
 * Only the backend's opaque, eligible outcome ID may be used as Google's deduplication key.
 */
export function sendVerifiedStripeTrialConversion(conversionId: string): boolean {
  const googleAdsId = import.meta.env.VITE_GOOGLE_ADS_ID || ''
  const sendTo = import.meta.env.VITE_GOOGLE_ADS_TRIAL_SEND_TO || ''

  if (
    !hasConsent('marketing') ||
    !isWebMeasurementAllowed() ||
    !/^[a-f0-9]{64}$/.test(conversionId) ||
    !googleAdsId ||
    !sendTo.startsWith(`${googleAdsId}/`) ||
    sendTo.includes('PASTE_CONVERSION_LABEL_HERE') ||
    sentTrialConversionIds.has(conversionId) ||
    persistedTrialConversions().includes(conversionId) ||
    !initializeGoogleTag()
  ) {
    return false
  }

  updateGoogleConsent(currentConsentState())
  window.gtag?.('event', 'conversion', {
    send_to: sendTo,
    transaction_id: conversionId,
  })
  sentTrialConversionIds.add(conversionId)
  try {
    window.localStorage.setItem(
      TRIAL_CONVERSIONS_KEY,
      JSON.stringify([...persistedTrialConversions(), conversionId].slice(-100))
    )
  } catch {
    // The stable transaction_id still provides Ads-side deduplication after refresh.
  }
  return true
}

function persistedTrialConversions(): string[] {
  try {
    const ids: unknown = JSON.parse(window.localStorage.getItem(TRIAL_CONVERSIONS_KEY) || '[]')
    return Array.isArray(ids)
      ? ids.filter((id): id is string => typeof id === 'string' && /^[a-f0-9]{64}$/.test(id))
      : []
  } catch {
    return []
  }
}

export function resetGoogleTagStateForTests(): void {
  defaultConsentSet = false
  googleTagConfigured = false
  googleTagScriptRequested = false
  sentTrialConversionIds.clear()
}
